import { createApi } from "@reduxjs/toolkit/query/react";
import {
  AddFranchiseOwnerPayload,
  AddRestaurantManagerPayload,
  Country,
  Currency,
  FranchiseOwner,
  FranchiseUpdatePayload,
  Restaurant,
  RestaurantFormData,
  RestaurantFranchise,
  RestaurantManager,
} from "./restaurant.type";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import { CollectionData } from "@/lib/type";
import { User } from "../user/user.type";
import { CreateFranchiseRestaurantPayload } from "@/lib/franchiseRestaurant";

export const RestaurantApi = createApi({
  reducerPath: "restaurantApi",
  tagTypes: ["restaurant", "restaurants", "employees", "franchiseOwners", "restaurantManagers", "franchise"],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    getRestaurants: build.query<Restaurant[], void>({
      query: () => "restaurants",
      providesTags: ["restaurants"],
      transformResponse: (response: CollectionData<Restaurant>) => {
        return response["hydra:member"];
      },
    }),
    createOrUpdateRestaurant: build.mutation<
      Restaurant,
      { id?: string; data: RestaurantFormData | FormData }
    >({
      query: ({ id, data }) => {
        if (id) {
          return {
            url: `restaurants/${id}`,
            method: "PATCH",
            body: JSON.stringify(data),
            headers: {
              "Content-Type": "application/merge-patch+json",
            },
          };
        }
        return {
          url: "restaurants",
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["restaurant"],
    }),
    // Owner restaurant creation (POST /restaurants is support-only, SFRESTO-139): no manager in the body; managers are assigned
    // afterwards through addRestaurantManager (SFAPI-214).
    createFranchiseRestaurant: build.mutation<
      Restaurant,
      { franchiseId: string; data: CreateFranchiseRestaurantPayload }
    >({
      query: ({ franchiseId, data }) => ({
        url: `restaurant_franchises/${franchiseId}/restaurants`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["restaurant", "restaurants"],
    }),
    updateRestaurant: build.mutation<
      Restaurant,
      { id: string; data: RestaurantFormData | FormData }
    >({
      query: ({ id, data }) => ({
        url: `restaurants/${id}`,
        method: "PATCH",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
      }),
      invalidatesTags: ["restaurant"],
    }),

    getCurrentRestaurant: build.query<Restaurant, void>({
      query: () => "restaurants/current",
    }),

    generateQrCode: build.mutation<Restaurant, void>({
      query: () => ({
        url: "qr_codes",
        method: "POST",
        headers: {
          "Content-Type": "application/ld+json",
        },
      }),
    }),

    getCurrentRestaurantFranchise: build.query<RestaurantFranchise, void>({
      query: () => ({
        url: "restaurant_franchises/current",
        method: "GET",
      }),
    }),
    // Franchises the current owner can switch between (SFRESTO-169). The
    // selected one is sent back as X-Franchise-Id by baseQuery.
    getMyFranchises: build.query<RestaurantFranchise[], void>({
      query: () => "restaurant_franchises/mine",
      transformResponse: (response: CollectionData<RestaurantFranchise>) =>
        response["hydra:member"],
    }),
    deleteRestaurant: build.mutation<Restaurant, { id: string }>({
      query: ({ id }) => ({
        url: `restaurants/${id}`,
        method: "DELETE",
      }),
    }),
    activateRestaurant: build.mutation<Restaurant, { id: string }>({
      query: ({ id }) => ({
        url: `restaurants/${id}/activate`,
        method: "PATCH",
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
        body: JSON.stringify({}),
      }),
    }),

    getEmployees: build.query<
      {
        employeeUser: User;
      }[],
      string
    >({
      query: (restaurantId) => `employees/${restaurantId}/waiters`,
      providesTags: ["employees"],
      transformResponse: (
        response: CollectionData<{
          employeeUser: User;
        }>
      ) => {
        return response["hydra:member"];
      },
    }),

    // Franchise Owners
    getFranchiseOwners: build.query<FranchiseOwner[], string>({
      query: (franchiseId) => `restaurant_franchises/${franchiseId}/owners`,
      providesTags: ["franchiseOwners"],
      transformResponse: (response: CollectionData<FranchiseOwner>) => {
        return response["hydra:member"];
      },
    }),
    addFranchiseOwner: build.mutation<
      FranchiseOwner,
      { franchiseId: string; data: AddFranchiseOwnerPayload }
    >({
      query: ({ franchiseId, data }) => ({
        url: `restaurant_franchises/${franchiseId}/owners`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["franchiseOwners"],
    }),
    removeFranchiseOwner: build.mutation<
      void,
      { franchiseId: string; ownerId: string }
    >({
      query: ({ franchiseId, ownerId }) => ({
        url: `restaurant_franchises/${franchiseId}/owners/${ownerId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["franchiseOwners"],
    }),

    // Restaurant Managers
    getRestaurantManagers: build.query<RestaurantManager[], string>({
      query: (restaurantId) => `restaurants/${restaurantId}/managers`,
      providesTags: ["restaurantManagers"],
      transformResponse: (response: CollectionData<RestaurantManager>) => {
        return response["hydra:member"];
      },
    }),
    addRestaurantManager: build.mutation<
      RestaurantManager,
      { restaurantId: string; data: AddRestaurantManagerPayload }
    >({
      query: ({ restaurantId, data }) => ({
        url: `restaurants/${restaurantId}/managers`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["restaurantManagers"],
    }),
    removeRestaurantManager: build.mutation<
      void,
      { restaurantId: string; managerId: string }
    >({
      query: ({ restaurantId, managerId }) => ({
        url: `restaurants/${restaurantId}/managers/${managerId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["restaurantManagers"],
    }),

    // Franchise Employees
    getFranchiseEmployees: build.query<
      { employeeUser: User }[],
      string
    >({
      query: (franchiseId) => `restaurant_franchises/${franchiseId}/employees`,
      providesTags: ["employees"],
      transformResponse: (
        response: CollectionData<{ employeeUser: User }>
      ) => {
        return response["hydra:member"];
      },
    }),

    // Countries & Currencies
    getCountries: build.query<Country[], void>({
      query: () => "countries",
      transformResponse: (response: CollectionData<Country>) => {
        return response["hydra:member"];
      },
    }),
    getCurrencies: build.query<Currency[], void>({
      query: () => "currencies",
      transformResponse: (response: CollectionData<Currency>) => {
        return response["hydra:member"];
      },
    }),

    // Activate Franchise
    activateFranchise: build.mutation<RestaurantFranchise, { id: string }>({
      query: ({ id }) => ({
        url: `restaurant_franchises/${id}/activate`,
        method: "PATCH",
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
        body: JSON.stringify({}),
      }),
      invalidatesTags: ["franchise"],
    }),

    // Update Franchise
    updateFranchise: build.mutation<
      RestaurantFranchise,
      { id: string; data: FranchiseUpdatePayload }
    >({
      query: ({ id, data }) => ({
        url: `restaurant_franchises/${id}`,
        method: "PATCH",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
      }),
      invalidatesTags: ["franchise"],
    }),
  }),
});

export const {
  useUpdateRestaurantMutation,
  useGenerateQrCodeMutation,
  useGetCurrentRestaurantQuery,
  useLazyGetCurrentRestaurantQuery,
  useGetRestaurantsQuery,
  useLazyGetRestaurantsQuery,
  useGetCurrentRestaurantFranchiseQuery,
  useLazyGetCurrentRestaurantFranchiseQuery,
  useGetMyFranchisesQuery,
  useLazyGetMyFranchisesQuery,
  useDeleteRestaurantMutation,
  useCreateOrUpdateRestaurantMutation,
  useCreateFranchiseRestaurantMutation,
  useActivateRestaurantMutation,
  useGetEmployeesQuery,
  useGetFranchiseOwnersQuery,
  useAddFranchiseOwnerMutation,
  useRemoveFranchiseOwnerMutation,
  useGetRestaurantManagersQuery,
  useAddRestaurantManagerMutation,
  useRemoveRestaurantManagerMutation,
  useGetFranchiseEmployeesQuery,
  useActivateFranchiseMutation,
  useUpdateFranchiseMutation,
  useGetCountriesQuery,
  useGetCurrenciesQuery,
} = RestaurantApi;
